import { useCallback, useEffect, useRef, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { getDownloadURL, ref } from 'firebase/storage';
import { deleteGuestVoiceNoteAdmin } from './guestVoiceNote';
import {
  getFirebaseAuth,
  getFirebaseStorage,
  getFirestoreDb,
  subscribeFirebaseAuth,
} from './firebaseRemoteSync';
import { useAdminTheme } from './adminTheme';

/**
 * @typedef {{
 *   id: string;
 *   storagePath: string;
 *   mimeType: string;
 *   durationSec: number;
 *   submittedAt: unknown;
 *   firstName?: string;
 *   email?: string;
 *   source?: string;
 * }} VoiceNoteRow
 */

function formatSubmittedAt(ts) {
  if (!ts || typeof ts.toDate !== 'function') return '—';
  try {
    return ts.toDate().toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return '—';
  }
}

function formatDuration(sec) {
  if (!Number.isFinite(sec) || sec <= 0) return '—';
  const s = Math.min(600, Math.max(0, sec));
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return m > 0 ? `${m}m ${r}s` : `${r}s`;
}

/** Object path only (no leading slash, no gs:// prefix). */
function normalizeStorageObjectPath(storagePath, docId) {
  let p = String(storagePath || `guestVoiceNotes/${docId}`).trim();
  p = p.replace(/^\/+/, '');
  const gs = p.match(/^gs:\/\/[^/]+\/(.+)$/);
  if (gs) return gs[1];
  return p;
}

export function AdminVoiceNotesTab() {
  const { theme } = useAdminTheme();
  const [notes, setNotes] = useState(/** @type {VoiceNoteRow[]} */ ([]));
  const [urlById, setUrlById] = useState(() => new Map());
  const [urlErrById, setUrlErrById] = useState(() => new Map());
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const urlGenRef = useRef(0);
  const rowsRef = useRef(/** @type {VoiceNoteRow[]} */ ([]));

  useEffect(() => {
    const db = getFirestoreDb();
    const storage = getFirebaseStorage();
    if (!db || !storage) {
      setError('Firebase Storage or Firestore is not configured for this build.');
      return undefined;
    }
    setError(null);

    /**
     * Storage rules require a signed-in admin. Firestore often delivers before Auth restores the
     * session from persistence; without authStateReady(), getDownloadURL fails and looked like
     * an infinite "Loading audio…" state.
     */
    const resolveUrls = async (rows, myGen) => {
      if (!rows.length) {
        if (urlGenRef.current === myGen) {
          setUrlById(new Map());
          setUrlErrById(new Map());
        }
        return;
      }

      const auth = getFirebaseAuth();
      if (auth) {
        try {
          await auth.authStateReady();
        } catch {
          /* ignore */
        }
      }
      if (auth && !auth.currentUser) {
        if (urlGenRef.current === myGen) {
          setUrlById(new Map(rows.map((r) => [r.id, ''])));
          setUrlErrById(new Map(rows.map((r) => [r.id, 'Sign in to load audio'])));
        }
        return;
      }

      const next = new Map();
      const errMap = new Map();
      for (const r of rows) {
        if (urlGenRef.current !== myGen) return;
        const path = normalizeStorageObjectPath(r.storagePath, r.id);
        try {
          const u = await getDownloadURL(ref(storage, path));
          next.set(r.id, u);
        } catch (e) {
          next.set(r.id, '');
          errMap.set(
            r.id,
            /** @type {import('firebase/storage').FirebaseError} */ (e)?.code ||
              /** @type {Error} */ (e)?.message ||
              'unknown',
          );
        }
      }
      if (urlGenRef.current === myGen) {
        setUrlById(new Map(next));
        setUrlErrById(new Map(errMap));
      }
    };

    const q = query(collection(db, 'guestVoiceNotes'), orderBy('submittedAt', 'desc'));

    const unsubAuth = subscribeFirebaseAuth(() => {
      const g = ++urlGenRef.current;
      void resolveUrls(rowsRef.current, g);
    });

    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => {
          const x = d.data();
          const firstName =
            typeof x.firstName === 'string' && x.firstName.trim() ? x.firstName.trim() : '';
          const email = typeof x.email === 'string' && x.email.trim() ? x.email.trim() : '';
          const source = typeof x.source === 'string' && x.source.trim() ? x.source.trim() : '';
          return {
            id: d.id,
            storagePath: String(x.storagePath || `guestVoiceNotes/${d.id}`),
            mimeType: String(x.mimeType || ''),
            durationSec: typeof x.durationSec === 'number' ? x.durationSec : 0,
            submittedAt: x.submittedAt ?? null,
            ...(firstName ? { firstName } : {}),
            ...(email ? { email } : {}),
            ...(source ? { source } : {}),
          };
        });
        rowsRef.current = rows;
        setNotes(rows);
        const myGen = ++urlGenRef.current;
        void resolveUrls(rows, myGen);
      },
      (err) => {
        setError(err?.message || String(err));
      },
    );
    return () => {
      urlGenRef.current += 1;
      unsubAuth();
      unsub();
    };
  }, []);

  const onDelete = useCallback(async (id, storagePath) => {
    if (!window.confirm('Delete this recording from Storage and the registry?')) return;
    try {
      await deleteGuestVoiceNoteAdmin(id, storagePath);
    } catch (e) {
      window.alert(e?.message || 'Delete failed');
    }
  }, []);

  const btnSecondary = {
    padding: '6px 12px',
    fontSize: 13,
    borderRadius: 6,
    border: `1px solid ${theme.border}`,
    background: theme.bgInput,
    color: theme.text,
    cursor: 'pointer',
  };

  return (
    <>
      <header
        style={{
          padding: '10px max(16px, env(safe-area-inset-right)) 12px max(16px, env(safe-area-inset-left))',
          borderBottom: `1px solid ${theme.border}`,
          background: theme.bgPanel,
        }}
      >
        <h1 id="admin-voice-notes-title" style={{ margin: 0, fontSize: 20, fontWeight: 700, color: theme.text }}>
          Voice notes
        </h1>
        <p style={{ margin: '6px 0 0', fontSize: 12, color: theme.muted, maxWidth: 560, lineHeight: 1.45 }}>
          Recordings submitted from the guest voicenote page. Newest first.
        </p>
      </header>
      <div
        style={{
          padding: '16px max(16px, env(safe-area-inset-right)) 40px max(16px, env(safe-area-inset-left))',
        }}
      >
        {error ? (
          <p style={{ color: theme.danger, margin: '0 0 16px', fontSize: 14 }} role="alert">
            {error}
          </p>
        ) : null}
        {!error && notes.length === 0 ? (
          <p style={{ color: theme.muted, margin: 0, fontSize: 14 }}>No voice notes yet.</p>
        ) : null}
        {notes.length > 0 ? (
          <ul className="admin-voice-notes-list" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
            {notes.map((n) => {
              const url = urlById.get(n.id) || '';
              const urlErr = urlErrById.get(n.id) || '';
              return (
                <li
                  key={n.id}
                  style={{
                    border: `1px solid ${theme.border}`,
                    borderRadius: 10,
                    padding: 14,
                    background: theme.bgPanel,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 10,
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: 10,
                    }}
                  >
                    <div style={{ fontSize: 13, color: theme.muted }}>
                      <span style={{ color: theme.text, fontWeight: 600 }}>{formatSubmittedAt(n.submittedAt)}</span>
                      {' · '}
                      {formatDuration(n.durationSec)}
                      {' · '}
                      <code style={{ fontSize: 12 }}>{n.mimeType || 'audio'}</code>
                    </div>
                    {n.firstName || n.email || n.source ? (
                      <p style={{ margin: '0 0 10px', fontSize: 13, color: theme.text, lineHeight: 1.4 }}>
                        {n.firstName ? (
                          <>
                            <strong style={{ color: theme.muted, fontWeight: 600 }}>Name</strong>{' '}
                            {n.firstName}
                          </>
                        ) : null}
                        {n.firstName && (n.email || n.source) ? ' · ' : null}
                        {n.email ? (
                          <>
                            <strong style={{ color: theme.muted, fontWeight: 600 }}>Email</strong>{' '}
                            <a href={`mailto:${encodeURIComponent(n.email)}`} style={{ color: theme.text }}>
                              {n.email}
                            </a>
                          </>
                        ) : null}
                        {(n.firstName || n.email) && n.source ? ' · ' : null}
                        {n.source ? (
                          <>
                            <strong style={{ color: theme.muted, fontWeight: 600 }}>Source</strong>{' '}
                            <code style={{ fontSize: 12 }}>{n.source}</code>
                          </>
                        ) : null}
                      </p>
                    ) : null}
                    <button
                      type="button"
                      style={{
                        ...btnSecondary,
                        color: theme.danger,
                        borderColor: theme.border,
                      }}
                      onClick={() => void onDelete(n.id, n.storagePath)}
                    >
                      Delete
                    </button>
                  </div>
                  {url ? (
                    <audio
                      controls
                      src={url}
                      preload="metadata"
                      style={{ width: '100%', maxWidth: 520, height: 40 }}
                    />
                  ) : urlErr ? (
                    <p style={{ margin: 0, fontSize: 13, color: theme.danger }}>
                      Couldn’t load audio{urlErr !== 'Sign in to load audio' ? `: ${urlErr}` : '.'}{' '}
                      {urlErr === 'storage/unauthorized' || urlErr === 'storage/unauthenticated'
                        ? 'Check Storage rules and that your Google account is in config/registryAdmins.'
                        : null}
                    </p>
                  ) : (
                    <p style={{ margin: 0, fontSize: 13, color: theme.muted }}>Loading audio…</p>
                  )}
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </>
  );
}
